import { Body1 } from "@iventis/styles";
import React, { PropsWithChildren } from "react";
import { FallbackProps } from "react-error-boundary";
import { SentryErrorBoundary, SentryFallbackRender } from "@iventis/observability-and-monitoring/src/error-boundary/sentry-error-boundary";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { Content } from "@iventis/translations";
import { CONTACT_URL, SUPPORT_EMAIL } from "@iventis/utilities";
import { createLog, isDynamicImportError } from "@iventis/observability-and-monitoring";
import { errorToErrorCode } from "./error-code";
import { StyledHeader1, StyledErrorImage, ErrorContainer, ButtonContainer, StyledButton, ErrorOverlay, StyledLink } from "./error-boundary-styles";
import { ErrorBoundaryFallbackVersion } from "./error-boundary-version";
import { ErrorBoundaryFallbackConnection } from "./error-boundary-connection";

export { ErrorBoundary } from "react-error-boundary";

type ErrorCallback = (errorCode: string, error: Error) => void;

export const ErrorBoundaryContext = React.createContext<{
    onError: ErrorCallback;
}>(null);

export type Action = "none" | "reload" | { action: () => void; actionText: string };
export type Size = "large" | "overlay" | "hidden" | "custom";

export interface ErrorBoundaryProps extends PropsWithChildren {
    action?: Action;
    size?: Size;
    onError?: (errorCode: string, error: Error) => void;
    className?: string;
    customComponent?: React.JSX.Element;
    testId?: string;
}

/**
 * React error boundary component that uses SentryErrorBoundary and uses our custom fallbacks.
 */
export const IventisErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children, action = "none", size = "large", onError, customComponent, className, testId }) => {
    const errorContext = React.useContext(ErrorBoundaryContext);

    const errorFallback: SentryFallbackRender = ({ error, resetError }) => {
        const errorCode = errorToErrorCode(error as Error);
        onError?.(errorCode, error as Error);
        errorContext?.onError(errorCode, error as Error);

        switch (size) {
            case "hidden":
                return null;
            case "large":
                return <ErrorBoundaryFallback error={error} resetErrorBoundary={resetError} action={action} className={className} testId={testId} />;
            case "custom":
                return customComponent;
            case "overlay":
            default:
                return <OverlayErrorBoundaryFallback className={className} />;
        }
    };

    return <SentryErrorBoundary fallback={errorFallback}>{children}</SentryErrorBoundary>;
};

export interface IventisFallbackProps extends FallbackProps {
    className?: string;
    action?: Action;
    testId?: string;
}

/**
 * Catch-all fallback component for use in error boundaries . If a dynamic import error is detected, it shows specific fallbacks for connection issues or version updates.
 * Otherwise, it displays a generic error message with support contact options.
 * Use GenericErrorBoundaryFallback if you don't want to displays these
 */
export const ErrorBoundaryFallback = ({ error, className, action, resetErrorBoundary, testId }: IventisFallbackProps) => {
    if (isDynamicImportError(error as Error)) {
        if (navigator.onLine === false) {
            return <ErrorBoundaryFallbackConnection />;
        }
        createLog("User encountered version modal");
        // If the user is online, we assume there's a new version available
        return <ErrorBoundaryFallbackVersion />;
    }
    return <GenericErrorBoundaryFallback error={error} resetErrorBoundary={resetErrorBoundary} className={className} action={action} testId={testId} />;
};

/**
 * Fallback with SVG Graphic and reload button
 */
export const GenericErrorBoundaryFallback = ({ error, className, action, testId }: IventisFallbackProps) => {
    const translate = useIventisTranslate();

    const errorCode = errorToErrorCode(error);

    return (
        <ErrorContainer className={className} data-testid={testId}>
            <StyledErrorImage />
            <StyledHeader1>{translate(Content.errors.error_boundaries.title_large)}</StyledHeader1>
            <Body1>
                {translate(Content.errors.error_boundaries.error_message)}
                <StyledLink href={`mailto:${SUPPORT_EMAIL}?subject=Error Code: ${errorCode}`}>{SUPPORT_EMAIL}</StyledLink> <br /> <br />
                <strong>{translate(Content.errors.error_boundaries.error_code, { code: errorCode })}</strong>
            </Body1>
            <ButtonContainer>
                <StyledButton href={CONTACT_URL} variant="outlined">
                    {translate(Content.errors.error_boundaries.contact)}
                </StyledButton>
                {action !== "none" && (
                    <StyledButton
                        onClick={() => {
                            if (action === "reload") {
                                window.location.reload();
                            } else {
                                action?.action();
                            }
                        }}
                    >
                        {action === "reload" ? <span>{translate(Content.errors.error_boundaries.reload)}</span> : <span>{action.actionText}</span>}
                    </StyledButton>
                )}
            </ButtonContainer>
        </ErrorContainer>
    );
};

/**
 * Fallback that simply shows a gray background
 */
export const OverlayErrorBoundaryFallback = ({ className }) => <ErrorOverlay className={className} />;
